import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { emailUnsubscribe } from '../services/emailService';

/**
 * Public one-click unsubscribe landing (from an email link: /unsubscribe?u=<uid>&t=<token>). Verifies the
 * signed token server-side and turns off notification emails — no login required.
 */
const UnsubscribePage: React.FC = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState<'working' | 'done' | 'error'>('working');

  useEffect(() => {
    const u = params.get('u') || '';
    const t = params.get('t') || '';
    if (!u || !t) { setState('error'); return; }
    emailUnsubscribe(u, t).then(() => setState('done')).catch(() => setState('error'));
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black px-6">
      <div className="w-full max-w-md rounded-2xl border border-secondary-200 dark:border-secondary-800 p-8 text-center">
        {state === 'working' && (
          <><Loader2 className="w-6 h-6 animate-spin mx-auto text-secondary-400" /><p className="mt-3 text-sm text-secondary-500">Updating your preferences…</p></>
        )}
        {state === 'done' && (
          <>
            <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-600" />
            <h1 className="mt-3 text-lg font-semibold text-secondary-900 dark:text-white">You're unsubscribed</h1>
            <p className="mt-1 text-sm text-secondary-500">You won't receive Wisetree notification emails anymore. You can turn them back on any time in your settings.</p>
          </>
        )}
        {state === 'error' && (
          <>
            <AlertCircle className="w-8 h-8 mx-auto text-secondary-500" />
            <h1 className="mt-3 text-lg font-semibold text-secondary-900 dark:text-white">That link didn't work</h1>
            <p className="mt-1 text-sm text-secondary-500">The unsubscribe link is invalid or expired. You can manage email notifications from your account settings.</p>
          </>
        )}
        <Link to="/" className="mt-5 inline-block rounded-xl bg-secondary-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-secondary-900 hover:opacity-90">Back to Wisetree AI</Link>
      </div>
    </div>
  );
};

export default UnsubscribePage;
